import React, { useRef, useState } from "react";
import { useController } from "react-hook-form";
import styles from "../styles.module.css";
import { formatBytes } from "../utils/formatters";
import { validateFiles, ALLOWED_EXTENSIONS } from "../utils/fileValidation";
import AppModal from "components/ui/AppModal/AppModal";

const ProjectFileUpload = ({ control, name, product, disabled }) => {
  const {
    field: { onChange, value: files = [] },
    fieldState: { error }
  } = useController({ name, control });

  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);
  const [feedbackModal, setFeedbackModal] = useState({
    open: false,
    variant: "custom",
    title: "",
    message: "",
    confirmText: "OK"
  });

  const closeFeedbackModal = () =>
    setFeedbackModal(prev => ({ ...prev, open: false }));

  const getAcceptAttribute = () => {
    if (!product || !ALLOWED_EXTENSIONS[product]) return undefined;
    return ALLOWED_EXTENSIONS[product].map(ext => `.${ext}`).join(",");
  };

  const processNewFiles = newFiles => {
    if (!newFiles || newFiles.length === 0) return;

    const renamedFiles = newFiles.map(file => {
      const hash = Date.now().toString(36) + Math.random().toString(36).substring(2, 7);
      const lastDot = file.name.lastIndexOf(".");
      const baseName = lastDot > 0 ? file.name.substring(0, lastDot) : file.name;
      const extension = lastDot > 0 ? file.name.substring(lastDot) : "";
      return new File([file], `${baseName}_${hash}${extension}`, {
        type: file.type,
        lastModified: file.lastModified
      });
    });

    const filesToAdd = [];
    for (const newFile of renamedFiles) {
      const prospective = [...files, ...filesToAdd, newFile];
      const { errorMessage } = validateFiles(prospective, product);
      if (errorMessage) {
        setFeedbackModal({
          open: true,
          variant: "warning",
          title: "Atenção",
          message: errorMessage,
          confirmText: "Entendi"
        });
        break;
      }
      filesToAdd.push(newFile);
    }

    if (filesToAdd.length > 0) {
      onChange([...files, ...filesToAdd]);
    }
  };

  const handleFileChange = e => {
    processNewFiles(Array.from(e.target.files));
    e.target.value = "";
  };

  const handleDrop = e => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled || !product) return;
    processNewFiles(Array.from(e.dataTransfer.files));
  };

  const removeFile = index => onChange(files.filter((_, i) => i !== index));

  const totalSize = files.reduce((acc, f) => acc + f.size, 0);
  const isDisabled = disabled || !product;

  return (
    <div className={styles.formGroup}>
      <label className={styles.formLabel}>Arquivos</label>

      <div
        className={`${styles.dropZone} ${isDragging ? styles.dropZoneActive : ""} ${isDisabled ? styles.dropZoneDisabled : ""}`}
        onDragOver={e => { e.preventDefault(); if (!isDisabled) setIsDragging(true); }}
        onDragEnter={e => { e.preventDefault(); if (!isDisabled) setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => !isDisabled && inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          onChange={handleFileChange}
          accept={getAcceptAttribute()}
          disabled={isDisabled}
          style={{ display: "none" }}
        />
        <i className={`fas fa-cloud-upload-alt ${styles.dropZoneIcon}`} />
        <p className={styles.dropZoneText}>
          {isDragging ? "Solte os arquivos aqui" : "Arraste arquivos ou clique para selecionar"}
        </p>
        <p className={styles.dropZoneHint}>
          {product
            ? `Formatos aceitos: ${ALLOWED_EXTENSIONS[product]?.join(", ").toUpperCase()}`
            : "Selecione um produto para habilitar o upload"}
        </p>
      </div>

      {files.length > 0 && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "0.5rem" }}>
            <span className={styles.fileUploadMeta}>
              {files.length} arquivo(s) — {formatBytes(totalSize)}
            </span>
            <button
              type="button"
              className={styles.buttonOutline}
              style={{ padding: "0.2rem 0.6rem", fontSize: "0.72rem", borderRadius: "6px" }}
              onClick={() => onChange([])}
            >
              Limpar tudo
            </button>
          </div>
          <ul className={styles.fileList}>
            {files.map((file, index) => (
              <li key={`${file.name}-${index}`} className={styles.fileListItem}>
                <i className="fas fa-file text-muted" style={{ fontSize: "0.8rem", flexShrink: 0 }} />
                <span title={file.name}>{file.name}</span>
                <span className={styles.fileListMeta}>{formatBytes(file.size)}</span>
                <button
                  type="button"
                  className={styles.fileRemoveButton}
                  onClick={() => removeFile(index)}
                  title={`Remover ${file.name}`}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {error && <p className={styles.errorMessage}>{error.message}</p>}

      <AppModal
        isOpen={feedbackModal.open}
        onClose={closeFeedbackModal}
        allowOutsideClick={true}
        variant={feedbackModal.variant}
        title={feedbackModal.title}
        message={feedbackModal.message}
        confirmText={feedbackModal.confirmText}
        cancelText={null}
        onConfirm={closeFeedbackModal}
        onCancel={closeFeedbackModal}
      />
    </div>
  );
};

export default ProjectFileUpload;
